import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { white, alto, mercury, emperor } from '@parchment/atoms';
import { animated, useSpring, useSpringRef, config } from '@react-spring/web';
import { interpolate } from 'flubber';
import { rgba } from 'polished';
const PATH_UNCHECKED = 'M0,0 L0,64 L64,64 L64,0 L0,0z'; //'M32,32';
const PATH_CHECKED = 'M25.9 47.8c-.4 0-.7-.1-1-.4L12.5 35c-.3-.3-.4-.6-.4-1 0-.6.2-.9.4-1.1l4.1-4.1c.3-.3.6-.5 1.1-.5s.7.1 1 .4l7.2 7.2 19.4-19.4c.3-.3.6-.5 1-.5s.7.1 1 .5l4.1 4.1c.2.2.4.5.4 1.1 0 .4-.1.8-.4 1L27 47.4c-.3.3-.7.4-1.1.4z';
const PATH_INDETERMINATE = 'M48.6 36.3H15.4c-1 0-1.7-.8-1.7-1.7v-5.2c0-1 .8-1.7 1.7-1.7h33.2c1 0 1.7.8 1.7 1.7v5.2c0 .9-.7 1.7-1.7 1.7z';

// the flubber interpolate function doesn't work in node (i.e. jest)...so if that blows up, we'll just return
// an interpolator function that just returns the final shape. While we could just mock it in jest for the checkbox
// tests, it would only fix tests that have it mocked. By handling the failure in the runtime code allows us to
// gracefully fallback to effectively no animation if we fail to generate an interpolator via flubber.
const _interpolate = (fromShape, toShape, options) => {
  try {
    return interpolate(fromShape, toShape, options);
  } catch (e) {
    return () => toShape;
  }
};

// memoize the component so that if/when it is re-rendered mid-animation with the same props that the animation doesn't
// get restarted
const Mark = /*#__PURE__*/React.memo(function Mark(props) {
  const {
    isReadOnly = false,
    isDisabled = false,
    isSelected = false,
    isIndeterminate = false
  } = props;
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [lastPath, setLastPath] = useState(getPath({
    isSelected,
    isIndeterminate
  }));
  const nextPath = getPath({
    isSelected,
    isIndeterminate
  });
  const nextPathInterpolator = useMemo(() => _interpolate(lastPath, nextPath, {
    maxSegmentLength: 5
  }), [lastPath, nextPath]);
  const transRef = useSpringRef();
  useEffect(() => {
    transRef.start();
    transRef.resume();
  }, [nextPath]);
  const {
    x
  } = useSpring({
    immediate: isFirstRender,
    config: config.stiff,
    reset: lastPath !== nextPath,
    onRest: () => setLastPath(nextPath),
    from: {
      x: 0
    },
    to: {
      x: 1
    }
  });
  useEffect(() => {
    setIsFirstRender(false);
  }, []);
  return /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 64 64"
  }, /*#__PURE__*/React.createElement(animated.path, {
    d: x.to(n => nextPathInterpolator(n)),
    fill: !isSelected && !isIndeterminate && (isDisabled || isReadOnly) ? mercury : white
  }));
});

/**
 * Checkbox icon representing the various states a checkbox can be displayed.
 * @param props
 * @constructor
 */
export const CheckboxIcon = props => {
  return /*#__PURE__*/React.createElement(IconBox, props, /*#__PURE__*/React.createElement(Mark, {
    isSelected: props.isSelected,
    isReadOnly: props.isReadOnly,
    isDisabled: props.isDisabled,
    isIndeterminate: props.isIndeterminate,
    color: props.color
  }));
};
function getPath(_ref) {
  let {
    isSelected,
    isIndeterminate
  } = _ref;
  if (isIndeterminate) {
    return PATH_INDETERMINATE;
  } else if (isSelected) {
    return PATH_CHECKED;
  }
  return PATH_UNCHECKED;
}
const ICON_SIZE = {
  'small': '18px',
  'medium': '24px',
  'large': '30px'
};
const BORDER_RADIUS = {
  'small': '2.5px',
  'medium': '3px',
  'large': '4px'
};
function getBgColor(_ref2) {
  let {
    isSelected,
    isIndeterminate,
    isDisabled,
    isReadOnly,
    color
  } = _ref2;
  const hasMark = isSelected || isIndeterminate;
  const visuallyDisabled = isDisabled || isReadOnly;
  if (visuallyDisabled) {
    return hasMark ? alto : mercury;
  } else {
    return color || emperor;
  }
}
function iconBoxStyles(_ref3) {
  let {
    scale = 'medium',
    isSelected,
    isIndeterminate,
    isDisabled,
    isReadOnly,
    color,
    isFocused,
    isHovered
  } = _ref3;
  const hasMark = isSelected || isIndeterminate;
  const visuallyDisabled = isDisabled || isReadOnly;
  const bgColor = getBgColor({
    isSelected,
    isIndeterminate,
    isDisabled,
    isReadOnly,
    color
  });
  const borderColor = hasMark ? bgColor : visuallyDisabled ? alto : color || alto;
  return css(["font-size:", ";border-radius:", ";background-color:", ";border-color:", ";box-shadow:", ";"], ICON_SIZE[scale], BORDER_RADIUS[scale], bgColor, borderColor, !isDisabled && (isFocused || isHovered) ? `0 0 6px 2px ${isReadOnly ? rgba(emperor, 0.5) : rgba(color || emperor, 0.5)}` : 'none');
}
const IconBox = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxIcon__IconBox",
  componentId: "xwsjig-0"
})(["display:inline-flex;align-items:center;justify-content:center;width:1em;height:1em;border-width:1px;border-style:solid;overflow:hidden;", ";svg{width:100%;height:100%;}"], iconBoxStyles);